import { useState } from 'react';
import { ArrowRight, ChevronDown, GripVertical, Lock, Settings2 } from 'lucide-react';
import type { ChatEnLista } from '@/api/frentes.api';
import { Avatar } from '@/components/ui/Avatar';
import { haceCuanto, nombreCorto } from '@/lib/athena';
import { cn } from '@/lib/cn';
import { SIN_CLASIFICAR, actividadDe, movimientos, tinte, ultimaLinea, type GrupoDeChats } from '@/lib/frentes';

/**
 * LAS DOS VISTAS NUEVAS DE «FRENTES» (tanda 6): mosaico y tablero.
 *
 * Las dos dibujan los MISMOS grupos (`chatsPorFrente`, en lib/frentes): un
 * frente es una tarjeta en el mosaico y una columna en el tablero, y «Sin
 * clasificar» va al final en las dos.
 *
 * ═══ NINGÚN FRENTE PESA MÁS QUE OTRO ═══
 *
 * Mismo tamaño, misma letra, mismo sitio para cada cosa: es la regla de esta
 * pantalla desde su primer día. Lo único propio de cada frente es su color, y
 * ese lo eligió el dueño.
 *
 * ═══ EL CANDADO ═══
 *
 * Con la llave echada, de un frente con candado se dibuja el nombre, el color y
 * un candado. Nada más: ni chats, ni la última línea, ni cuántos hay sin leer.
 * No se esconden con CSS, no se dibujan: los nombres no llegan al HTML.
 */

interface Comun {
  grupos: GrupoDeChats[];
  /** El candado está abierto: los frentes con llave enseñan lo suyo. */
  candadoAbierto: boolean;
  ahora: Date;
  /** Lo que hay escrito en el buscador: un grupo sin nada dice que es por eso. */
  buscando?: string;
}

const cuantosChats = (n: number) => (n === 1 ? '1 chat' : `${n} chats`);
const tapado = (g: GrupoDeChats, candadoAbierto: boolean) => g.conCandado && !candadoAbierto;

const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600';

/** Cuántos mensajes sin leer. El número solo no dice de qué: lo dice el `title` y el lector de pantalla. */
function SinLeer({ n, largo = false, className }: { n: number; largo?: boolean; className?: string }) {
  if (n <= 0) return null;
  return (
    <span
      title={`${n} sin leer`}
      className={cn('shrink-0 rounded-full bg-brand-600 px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums text-white', className)}
    >
      {n}
      {largo ? ' sin leer' : <span className="sr-only"> sin leer</span>}
    </span>
  );
}

function AvatarDeChat({ c }: { c: ChatEnLista }) {
  // La foto no se arrastra sola (en el tablero, lo que se lleva el cursor es la tarjeta entera).
  return (
    <span className="shrink-0 [&_img]:pointer-events-none">
      <Avatar name={nombreCorto(c.nombre)} src={c.avatarUrl} isGroup={c.esGrupo} seed={c.telefono ?? c.id} size="sm" />
    </span>
  );
}

function PuntoDeColor({ color, className }: { color: string; className?: string }) {
  return <span aria-hidden className={cn('shrink-0 rounded-full', className)} style={{ backgroundColor: color }} />;
}

// ═══════════════════════════════════════════════════════════════════════════
// MOSAICO
// ═══════════════════════════════════════════════════════════════════════════

/** Cuántos chats se asoman en cada tarjeta. */
const A_LA_VISTA = 3;

function TarjetaDeFrente({ g, cerrado, ahora, buscando, onElegir }: { g: GrupoDeChats; cerrado: boolean; ahora: Date; buscando: string; onElegir: () => void }) {
  const esSin = g.id === SIN_CLASIFICAR;
  const n = g.chats.length;
  const mas = n - A_LA_VISTA;
  return (
    // Toda la tarjeta es UN botón: tocarla es entrar al frente. Por dentro, solo texto.
    <button
      type="button"
      data-frente={g.id}
      data-cerrado={cerrado ? 'si' : undefined}
      onClick={onElegir}
      className={cn(
        'group relative flex h-full w-full flex-col overflow-hidden rounded-xl border bg-white text-left shadow-sm dark:bg-slate-900',
        'transition-[transform,box-shadow] duration-200 ease-out hover:shadow-md motion-safe:hover:-translate-y-0.5',
        // «Sin clasificar» no es un frente: borde punteado, como su píldora en la lista.
        esSin ? 'border-dashed border-slate-300 dark:border-slate-600' : 'border-surface-border dark:border-slate-800',
        FOCO,
      )}
    >
      <span className="block px-4 py-3" style={{ backgroundColor: tinte(g.color) }}>
        <span className="flex items-center gap-2.5">
          <PuntoDeColor color={g.color} className="h-3 w-3" />
          <span className="min-w-0 flex-1 truncate text-base font-semibold text-slate-900 dark:text-slate-50">{g.nombre}</span>
          {cerrado ? (
            <Lock aria-hidden className="h-4 w-4 shrink-0 text-slate-600 dark:text-slate-300" />
          ) : (
            g.ultimaActividad && <span className="shrink-0 text-xs text-slate-600 dark:text-slate-300">{haceCuanto(g.ultimaActividad, ahora)}</span>
          )}
        </span>
        <span className="mt-1 flex min-h-[1.25rem] items-center gap-2 pl-[1.375rem] text-sm text-slate-700 dark:text-slate-200">
          {cerrado ? (
            'Con candado'
          ) : (
            <>
              <span className="tabular-nums">{cuantosChats(n)}</span>
              <SinLeer n={g.sinLeer} largo />
            </>
          )}
        </span>
      </span>

      {cerrado ? (
        <span className="flex flex-1 items-center px-4 py-5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          Lo que hay dentro no se enseña hasta que lo abras.
        </span>
      ) : n === 0 ? (
        <span className="flex flex-1 items-center px-4 py-5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {buscando
            ? `Nada con "${buscando}" aquí.`
            : esSin
              ? 'Todo está clasificado. Aquí cae lo que no esté en ningún frente.'
              : 'Todavía no tiene chats. Pásale alguno desde el tablero, o con la carpeta de la lista.'}
        </span>
      ) : (
        <span className="flex flex-1 flex-col px-4 py-2">
          {g.chats.slice(0, A_LA_VISTA).map((c) => (
            <span key={c.id} data-chat={c.id} className="flex items-center gap-2.5 py-1.5">
              <AvatarDeChat c={c} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">{nombreCorto(c.nombre)}</span>
                <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{ultimaLinea(c)}</span>
              </span>
              <SinLeer n={c.sinLeer} />
            </span>
          ))}
        </span>
      )}

      <span className="flex items-center gap-2 border-t border-surface-border px-4 py-2.5 text-xs dark:border-slate-800">
        {!cerrado && mas > 0 && <span className="tabular-nums text-slate-500 dark:text-slate-400">y {mas} más</span>}
        <span className="ml-auto inline-flex items-center gap-1 font-medium text-brand-700 dark:text-brand-300">
          {cerrado ? 'Abrir con tu PIN' : 'Ver en la lista'}
          <ArrowRight aria-hidden className="h-3.5 w-3.5 transition-transform duration-200 ease-out motion-safe:group-hover:translate-x-0.5" />
        </span>
      </span>
    </button>
  );
}

/**
 * EL MOSAICO: «¿cómo está cada frente?». Una tarjeta por frente con su color,
 * cuántos chats tiene, cuántos sin leer, hace cuánto se movió y sus tres chats
 * más recientes. Tocarla abre ese frente en la lista. Al final, «Sin
 * clasificar» y la puerta a administrar los frentes.
 * Sin estado: se prueba con renderToStaticMarkup.
 */
export function MosaicoDeFrentes({
  grupos,
  candadoAbierto,
  ahora,
  buscando = '',
  onElegir,
  onAdministrar,
}: Comun & {
  /** Con el id del grupo: el de un frente, o `SIN_CLASIFICAR`. */
  onElegir: (grupoId: string) => void;
  onAdministrar: () => void;
}) {
  return (
    <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {grupos.map((g) => (
        <li key={g.id} className="min-w-0">
          <TarjetaDeFrente g={g} cerrado={tapado(g, candadoAbierto)} ahora={ahora} buscando={buscando} onElegir={() => onElegir(g.id)} />
        </li>
      ))}
      <li className="min-w-0">
        <button
          type="button"
          data-administrar
          onClick={onAdministrar}
          className={cn(
            'flex h-full min-h-[5.5rem] w-full items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-4 text-left transition-colors',
            'hover:border-slate-400 hover:bg-white dark:border-slate-700 dark:hover:border-slate-600 dark:hover:bg-slate-900',
            FOCO,
          )}
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-200/70 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <Settings2 aria-hidden className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Administrar frentes</span>
            <span className="block text-xs leading-relaxed text-slate-600 dark:text-slate-400">Crea otro, o cámbiales el nombre, el color o el candado.</span>
          </span>
        </button>
      </li>
    </ul>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TABLERO
// ═══════════════════════════════════════════════════════════════════════════

/** El tipo propio del arrastre: una columna solo recibe tarjetas de este tablero, no un archivo ni un texto cualquiera. */
const TIPO_DE_ARRASTRE = 'application/x-conversia-chat';

/** Un chat en una columna: compacto. Sin estado: se prueba con renderToStaticMarkup. */
function TarjetaDeChat({
  c,
  grupo,
  destinos,
  ahora,
  apagada,
  onAbrir,
  onMover,
  onArrastrar,
  onDejar,
}: {
  c: ChatEnLista;
  /** La columna en que está dibujada (un chat en dos frentes sale en dos columnas). */
  grupo: GrupoDeChats;
  /** A dónde se puede mover desde aquí. */
  destinos: GrupoDeChats[];
  ahora: Date;
  /** Va en el aire, ya sobre una columna: se apaga en su sitio hasta que la suelten. */
  apagada: boolean;
  onAbrir: () => void;
  onMover: (a: string) => void;
  onArrastrar: () => void;
  onDejar: () => void;
}) {
  const nombre = nombreCorto(c.nombre);
  const cuando = actividadDe(c);
  const tambienEn = c.frentes.filter((f) => f.id !== grupo.id);
  return (
    <article
      data-chat={c.id}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(TIPO_DE_ARRASTRE, c.id);
        onArrastrar();
      }}
      onDragEnd={onDejar}
      // El nombre es el botón (para el teclado y el lector de pantalla); con el dedo o el cursor vale toda la tarjeta.
      // Y es SOLO el nombre: Firefox no deja empezar un arrastre desde dentro de un botón.
      onClick={onAbrir}
      className={cn(
        // `relative`: lo que va solo para el lector de pantalla es de posición absoluta, y sin esto se
        // mide contra la página: en una columna que quedó fuera de la vista, la hacía rodar de lado.
        'group relative cursor-pointer select-none rounded-lg border border-surface-border bg-white p-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-800',
        'transition-[transform,box-shadow,opacity] duration-200 ease-out hover:shadow-md motion-safe:hover:-translate-y-0.5',
        apagada && 'opacity-40',
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex">
          <AvatarDeChat c={c} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              title={`Abrir el chat de ${nombre}`}
              className={cn('block min-w-0 truncate rounded text-left text-sm font-semibold leading-snug text-slate-900 group-hover:text-brand-700 dark:text-slate-100 dark:group-hover:text-brand-300', FOCO)}
            >
              {nombre}
            </button>
            <SinLeer n={c.sinLeer} className="ml-auto" />
          </div>
          <p className="line-clamp-2 text-xs leading-snug text-slate-500 dark:text-slate-400">{ultimaLinea(c)}</p>
        </div>
        <GripVertical aria-hidden className="hidden h-4 w-4 shrink-0 cursor-grab text-slate-300 transition-colors group-hover:text-slate-500 md:block dark:text-slate-600" />
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 md:mt-2">
        <p className="flex min-w-0 items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
          {cuando && <span className="truncate">{haceCuanto(cuando, ahora)}</span>}
          {/* Si también está en otro frente, se dice: por eso sale en dos columnas. */}
          {tambienEn.map((f) => (
            <span key={f.id} title={`También en ${f.nombre}`} className="flex shrink-0">
              <PuntoDeColor color={f.color} className="h-2 w-2" />
              <span className="sr-only">También en {f.nombre}</span>
            </span>
          ))}
        </p>
        {destinos.length > 0 && (
          // Un `<select>` de verdad, invisible, encima del rótulo: el teléfono abre su selector nativo y lo que se toca mide 44 px.
          <span className="relative inline-flex min-h-[44px] shrink-0 items-center md:min-h-0" onClick={(e) => e.stopPropagation()}>
            <select
              aria-label={`Mover el chat de ${nombre} a otro frente`}
              value=""
              onChange={(e) => {
                const a = e.target.value;
                // Vuelve a su rótulo: si el cambio falla, no queda marcando lo que no es.
                e.target.value = '';
                if (a) onMover(a);
              }}
              className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none text-base opacity-0"
            >
              <option value="" disabled hidden>
                Mover a…
              </option>
              {destinos.map((d) => (
                <option key={d.id} value={d.id}>
                  {/* Si está en varios frentes, sacarlo de este no lo deja «sin clasificar»: se dice lo que pasa. */}
                  {d.id === SIN_CLASIFICAR && tambienEn.length > 0 ? `Sacarlo de ${grupo.nombre}` : d.nombre}
                </option>
              ))}
            </select>
            <span
              aria-hidden
              className={
                'pointer-events-none inline-flex h-7 items-center gap-0.5 rounded-md border border-surface-border pl-2 pr-1 text-xs font-medium text-slate-600 ' +
                'transition-colors peer-hover:border-slate-300 peer-hover:bg-slate-100 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 ' +
                'dark:border-slate-600 dark:text-slate-300 dark:peer-hover:bg-slate-700'
              }
            >
              Mover a…
              <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </span>
          </span>
        )}
      </div>
    </article>
  );
}

/**
 * EL TABLERO: «¿dónde está cada chat?». Una columna por frente y, al final,
 * «Sin clasificar». En el teléfono se pasan de lado, una casi completa a la
 * vez; en escritorio caben las cinco de arranque y, si el dueño abre más
 * frentes, el tablero rueda.
 *
 * Arrastrar un chat a otra columna lo cambia de frente (qué se manda lo decide
 * `movimientos`; guardarlo y volver atrás si el servidor no quiere es de quien
 * recibe `onMover`). Arrastre nativo del navegador, sin librerías. En el
 * teléfono no hay arrastre: cada tarjeta trae «Mover a…», que es también el
 * camino con el teclado.
 *
 * Solo recuerda qué tarjeta va en el aire y sobre qué columna está.
 */
export function TableroDeFrentes({
  grupos,
  candadoAbierto,
  ahora,
  buscando = '',
  onAbrir,
  onMover,
  onAbrirCandado,
}: Comun & {
  onAbrir: (chatId: string) => void;
  /** La tarjeta se soltó en esa columna, o se eligió en su «Mover a…». `de` y `a` son ids de grupo. */
  onMover: (chat: ChatEnLista, de: string, a: string) => void;
  /** Se quiere abrir un frente con candado sin salir del tablero. */
  onAbrirCandado: (grupoId: string) => void;
}) {
  // Un chat puede estar en dos columnas: lo que va en el aire es el chat Y de cuál salió.
  const [enElAire, setEnElAire] = useState<{ chat: ChatEnLista; de: string } | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);
  const abiertos = grupos.filter((g) => !tapado(g, candadoAbierto));
  // Hacia un frente cerrado no se mueve nada; desde él tampoco: sus chats ni se dibujan.
  const sePuede = (chat: ChatEnLista, de: string, a: GrupoDeChats) => !tapado(a, candadoAbierto) && movimientos(chat, de, a.id).length > 0;
  const dejar = () => {
    setEnElAire(null);
    setSobre(null);
  };

  return (
    // En el teléfono cada columna se acomoda al borde; con mouse, sin imán.
    <div className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:snap-none md:scroll-px-0 md:px-0">
      {grupos.map((g) => {
        const cerrado = tapado(g, candadoAbierto);
        const acepta = enElAire ? sePuede(enElAire.chat, enElAire.de, g) : false;
        const encima = acepta && sobre === g.id;
        return (
          <section
            key={g.id}
            data-columna={g.id}
            data-cerrado={cerrado ? 'si' : undefined}
            data-soltar={encima ? 'si' : undefined}
            aria-labelledby={`frente-${g.id}`}
            onDragOver={(e) => {
              if (!acepta || !Array.from(e.dataTransfer.types).includes(TIPO_DE_ARRASTRE)) return;
              // Sin `preventDefault` el navegador no deja soltar: es lo que se quiere en las demás columnas.
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setSobre(g.id);
            }}
            onDragLeave={(e) => {
              // Pasar por encima de una tarjeta de la columna no es salir de la columna.
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSobre((s) => (s === g.id ? null : s));
            }}
            onDrop={(e) => {
              e.preventDefault();
              const soltada = enElAire;
              dejar();
              if (soltada && sePuede(soltada.chat, soltada.de, g)) onMover(soltada.chat, soltada.de, g.id);
            }}
            className={cn(
              'flex w-[86%] shrink-0 snap-start flex-col overflow-hidden rounded-xl border sm:w-72 xl:w-auto xl:min-w-[13.5rem] xl:flex-1',
              // Con muchos chats se desplaza la columna y no la página: los encabezados siguen a la vista para soltar.
              'max-h-[max(22rem,calc(100dvh-19.5rem-var(--alto-de-pestanas,0px)))] md:max-h-[max(24rem,calc(100dvh-14.5rem-var(--alto-de-pestanas,0px)))]',
              'transition-[background-color,border-color,box-shadow] duration-150',
              encima
                ? 'border-brand-400 bg-brand-50 ring-2 ring-brand-400/40 dark:bg-brand-900/20'
                : acepta
                  ? 'border-dashed border-brand-400 bg-slate-100/80 dark:bg-slate-900/60'
                  : 'border-surface-border bg-slate-100/80 dark:border-slate-800 dark:bg-slate-900/60',
            )}
          >
            {/* En dos renglones: con cinco columnas en pantalla, el nombre del frente no comparte el suyo con los contadores. */}
            <header className="px-3 py-2.5" style={{ backgroundColor: tinte(g.color) }}>
              <div className="flex items-center gap-2">
                <PuntoDeColor color={g.color} className="h-2.5 w-2.5" />
                <h2 id={`frente-${g.id}`} className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900 dark:text-slate-50">
                  {g.nombre}
                </h2>
                {cerrado && <Lock aria-hidden className="h-4 w-4 shrink-0 text-slate-600 dark:text-slate-300" />}
              </div>
              <p className="mt-0.5 flex min-h-[1.125rem] items-center gap-2 pl-[1.125rem] text-xs text-slate-600 dark:text-slate-300">
                {cerrado ? (
                  'Con candado'
                ) : (
                  <>
                    <span data-cuenta={g.chats.length} className="tabular-nums">
                      {cuantosChats(g.chats.length)}
                    </span>
                    <SinLeer n={g.sinLeer} largo />
                  </>
                )}
              </p>
            </header>

            {cerrado ? (
              <div className="flex flex-col items-center gap-3 px-4 py-6 text-center">
                <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-400">Tiene candado. Sus chats no se enseñan hasta que lo abras.</p>
                <button
                  type="button"
                  onClick={() => onAbrirCandado(g.id)}
                  className={cn(
                    'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-surface-border bg-white px-3.5 text-sm font-medium text-slate-700 transition-colors md:min-h-[36px]',
                    'hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
                    FOCO,
                  )}
                >
                  <Lock aria-hidden className="h-3.5 w-3.5" />
                  Abrir {g.nombre}
                </button>
              </div>
            ) : (
              // El relleno de arriba le deja sitio a la tarjeta que sube un punto con el cursor.
              <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-2 pt-2">
                {g.chats.length === 0 ? (
                  <li
                    className={cn(
                      'rounded-lg border border-dashed px-3 py-5 text-center text-xs leading-relaxed',
                      encima ? 'border-brand-400 font-medium text-brand-700 dark:text-brand-200' : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-400',
                    )}
                  >
                    {encima
                      ? 'Suéltalo aquí'
                      : buscando
                        ? `Nada con "${buscando}" aquí.`
                        : g.id === SIN_CLASIFICAR
                          ? 'Todo está clasificado. Un chat que saques de su frente cae aquí.'
                          : 'Todavía no tiene chats. Arrastra uno hasta aquí, o usa su «Mover a…».'}
                  </li>
                ) : (
                  g.chats.map((c) => (
                    <li key={c.id}>
                      <TarjetaDeChat
                        c={c}
                        grupo={g}
                        destinos={abiertos.filter((d) => sePuede(c, g.id, d))}
                        ahora={ahora}
                        // Se apaga cuando ya va sobre la columna que la recibe, no al agarrarla: el navegador
                        // le toma justo entonces la foto que sigue al cursor.
                        apagada={Boolean(sobre) && enElAire?.chat.id === c.id && enElAire.de === g.id}
                        onAbrir={() => onAbrir(c.id)}
                        onMover={(a) => onMover(c, g.id, a)}
                        onArrastrar={() => setEnElAire({ chat: c, de: g.id })}
                        onDejar={dejar}
                      />
                    </li>
                  ))
                )}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
